// pkg91a — MightyDiscovery™ rename (the mark REASSIGNED to this tool from the Analyzer — it is the one that drafts discovery): page title branded with ™ and a plain-English tail; all functional copy, review flow, and generated documents untouched (pkg83a absolute) — delivered 2026-07-18
// pkg75 — cost recovery on the confirm dialog: "Bill this draft to: [— Firm pays —▾] as an expense · $1.00" (per-instance opt-in; picking a client sends clientId + bill_to_client with the draft) — delivered 2026-07-13
// pkg65 — credit-model banner/confirm/receipt: 2 free AI actions then 1 credit per draft (flat — OCR no longer costs extra); per-run dollar pricing UI retired (D-65g) — delivered 2026-07-11
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

// Tool B — AI-assisted outgoing discovery.
//
// One page, two phases (drafts are not persisted server-side, so the review
// lives in this session from /draft until /generate):
//   1) UPLOAD — choose mode, upload the complaint (+ optional supporting docs /
//      style exemplar), type optional objectives. POST /api/discovery/ai/draft.
//   2) REVIEW — edit / exclude / delete the AI-drafted interrogatories & RFPs,
//      fill the case caption + signing attorneys, acknowledge the review gate,
//      then POST /api/discovery/ai/generate to download the .docx.
//
// Gated by `generate_discovery` (route). pkg65: the per-firm
// ai_discovery_enabled flag is retired — every firm has Tool B, metered by the
// unified AI credit system (2 free lifetime actions, then 1 credit per draft).
// A 403 from /draft (AI_NEEDS_SUBSCRIPTION / AI_NO_CREDITS) is shown as-is.

const TN_DEFAULT_COURT = 'CIRCUIT COURT FOR HAMILTON COUNTY, TENNESSEE';

export default function AdminAIDiscoveryPage() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState('upload'); // 'upload' | 'review'
  const [error, setError] = useState('');

  // ── upload phase ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState('defend');
  const [complaintFile, setComplaintFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [exemplarFile, setExemplarFile] = useState(null);
  const [objectives, setObjectives] = useState('');
  // Multi-party upload-phase state.
  const [uploadPlaintiffs, setUploadPlaintiffs] = useState(['']);
  const [uploadDefendants, setUploadDefendants] = useState(['']);
  const [uploadCrossParties, setUploadCrossParties] = useState([]); // [{name,role}]
  const [uploadCourt, setUploadCourt] = useState(TN_DEFAULT_COURT);
  const [drafting, setDrafting] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // pkg75 — cost recovery: optionally bill this draft to a client as an expense
  const [billClients, setBillClients]   = useState(null); // lazy client list
  const [billClientId, setBillClientId] = useState('');   // '' = firm pays
  const [billAmount, setBillAmount]     = useState('1.00');

  // ── review phase ──────────────────────────────────────────────────────────
  const [draftMeta, setDraftMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [plaintiffs, setPlaintiffs] = useState(['']);
  const [defendants, setDefendants] = useState(['']);
  const [crossParties, setCrossParties] = useState([]); // [{name,role}]
  const [caseFields, setCaseFields] = useState({
    court: TN_DEFAULT_COURT, docketNo: '', division: '',
    representingParty: '', directedTo: '',
    opposingCounsel: '', hasChildren: false,
  });
  const [attorneys, setAttorneys] = useState([]);
  const [attorneyIds, setAttorneyIds] = useState([]);
  const [signingAttorneyId, setSigningAttorneyId] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    adminApi.getAdminUsers().then((users) => {
      const roster = (users || []).filter((u) => u.role === 'owner' || u.role === 'attorney');
      setAttorneys(roster);
      const withBar = roster.filter((u) => u.bar_number).map((u) => u.id);
      const defaults = withBar.length ? withBar : roster.map((u) => u.id);
      setAttorneyIds(defaults);
      setSigningAttorneyId(defaults[0] || '');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    adminApi.getAiDiscoveryPricing().then(setPricing).catch(() => {});
  }, []);

  const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2).replace(/\.00$/, '');

  function setField(k, v) { setCaseFields((p) => ({ ...p, [k]: v })); }

  // Party list helpers.
  function updateParty(setter, idx, val) {
    setter((prev) => prev.map((v, i) => i === idx ? val : v));
  }
  function addParty(setter) { setter((prev) => [...prev, '']); }
  function removeParty(setter, idx) {
    setter((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  }
  function updateCrossParty(idx, field, val) {
    setCrossParties((prev) => prev.map((cp, i) => i === idx ? { ...cp, [field]: val } : cp));
  }
  function addCrossParty() { setCrossParties((prev) => [...prev, { name: '', role: '' }]); }
  function removeCrossParty(idx) { setCrossParties((prev) => prev.filter((_, i) => i !== idx)); }

  // All named parties in the current review state — used to populate dropdowns.
  function allPartyNames() {
    return [
      ...plaintiffs.filter(Boolean),
      ...defendants.filter(Boolean),
      ...crossParties.filter(cp => cp.name).map(cp => cp.name),
    ];
  }

  function onDraftClick(e) {
    if (e) e.preventDefault();
    setError('');
    if (!complaintFile) { setError('Please choose the complaint file.'); return; }
    if (!uploadPlaintiffs.some(Boolean)) { setError('Please enter at least one plaintiff.'); return; }
    if (!uploadDefendants.some(Boolean)) { setError('Please enter at least one defendant.'); return; }
    setConfirmOpen(true);
  }

  async function runDraft() {
    setConfirmOpen(false);
    setError('');
    if (!complaintFile) { setError('Please choose the complaint file.'); return; }
    setDrafting(true);
    try {
      const fd = new FormData();
      fd.append('complaint', complaintFile);
      supportingFiles.forEach((f) => fd.append('supporting', f));
      if (exemplarFile) { fd.append('exemplar', exemplarFile); fd.append('exemplarSource', 'uploaded'); }
      fd.append('mode', mode);
      if (objectives.trim()) fd.append('attorneyGuidance', objectives.trim());
      if (uploadCourt) fd.append('court', uploadCourt);
      // Send multi-party arrays.
      uploadPlaintiffs.filter(Boolean).forEach((p) => fd.append('plaintiffs[]', p));
      uploadDefendants.filter(Boolean).forEach((d) => fd.append('defendants[]', d));
      uploadCrossParties.filter(cp => cp.name).forEach((cp) => {
        fd.append('crossParties[]', JSON.stringify(cp));
      });
      // pkg75 — cost recovery: bill this draft to the picked client.
      if (billClientId) {
        fd.append('clientId', billClientId);
        fd.append('bill_to_client', JSON.stringify({
          enabled: true,
          amount_cents: Math.round(parseFloat(billAmount || '1') * 100),
        }));
      }

      const data = await adminApi.aiDiscoveryDraft(fd);
      setDraftMeta({
        model: data.model,
        counts: data.counts || { interrogatories: 0, requests: 0 },
        truncated: data.truncated || {},
        extractionWarnings: data.extractionWarnings,
        exemplarSource: data.exemplarSource,
        ocrUsed: data.ocrUsed,
        aiUsage: data.aiUsage, // pkg65 — { source: 'free'|'credit'|'exempt', freeRemaining, creditBalance }
        mode: data.mode,
      });
      setQuestions((data.questions || []).map((q) => ({ ...q, include: true })));

      // Pre-fill review phase from draft response.
      const respPlaintiffs = data.plaintiffs && data.plaintiffs.length ? data.plaintiffs : uploadPlaintiffs.filter(Boolean);
      const respDefendants = data.defendants && data.defendants.length ? data.defendants : uploadDefendants.filter(Boolean);
      const respCross = data.crossParties || uploadCrossParties.filter(cp => cp.name);
      setPlaintiffs(respPlaintiffs.length ? respPlaintiffs : ['']);
      setDefendants(respDefendants.length ? respDefendants : ['']);
      setCrossParties(respCross);

      // Default representingParty: first party on our side per mode.
      const defaultRep = mode === 'defend'
        ? (respDefendants[0] || '')
        : (respPlaintiffs[0] || '');
      // Default directedTo: first party on opposing side.
      const defaultTo = mode === 'defend'
        ? (respPlaintiffs[0] || '')
        : (respDefendants[0] || '');

      setCaseFields((p) => ({
        ...p,
        court: data.court || uploadCourt || p.court,
        representingParty: defaultRep,
        directedTo: defaultTo,
      }));
      setPhase('review');
      window.scrollTo(0, 0);
    } catch (err) {
      const notCharged = pricing && !pricing.exempt ? ' No credit or free action was used.' : '';
      setError((err.message || 'Drafting failed.') + notCharged);
    } finally {
      setDrafting(false);
    }
  }

  function updateQuestion(idx, patch) {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }
  function removeQuestion(idx) {
    setQuestions((qs) => qs.filter((_, i) => i !== idx));
  }
  function toggleAttorney(id) {
    setAttorneyIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  function startOver() {
    setPhase('upload');
    setQuestions([]);
    setDraftMeta(null);
    setAcknowledged(false);
    setError('');
    setPlaintiffs(['']);
    setDefendants(['']);
    setCrossParties([]);
    setCaseFields({ court: TN_DEFAULT_COURT, docketNo: '', division: '', representingParty: '', directedTo: '', opposingCounsel: '', hasChildren: false });
  }

  async function handleGenerate() {
    setError('');
    const included = questions.filter((q) => q.include && q.text.trim());
    if (!included.length) { setError('Include at least one question to generate.'); return; }
    if (!plaintiffs.some(Boolean)) { setError('At least one plaintiff is required.'); return; }
    if (!defendants.some(Boolean)) { setError('At least one defendant is required.'); return; }
    if (!caseFields.representingParty) { setError('Please select which party your firm represents.'); return; }
    if (!caseFields.directedTo) { setError('Please select which party discovery is directed to.'); return; }
    if (!attorneyIds.length) { setError('Select at least one signing attorney.'); return; }
    if (!acknowledged) { setError('Please confirm you have reviewed the requests before generating.'); return; }
    setGenerating(true);
    try {
      const blob = await adminApi.aiDiscoveryGenerate({
        questions: included.map((q) => ({ type: q.type, text: q.text.trim() })),
        plaintiffs: plaintiffs.filter(Boolean),
        defendants: defendants.filter(Boolean),
        crossParties: crossParties.filter(cp => cp.name),
        representingParty: caseFields.representingParty,
        directedTo: caseFields.directedTo,
        court: caseFields.court,
        docketNo: caseFields.docketNo,
        division: caseFields.division,
        hasChildren: caseFields.hasChildren,
        opposingCounsel: caseFields.opposingCounsel,
        attorneyIds,
        signingAttorneyId,
      });
      const safeName = String(caseFields.directedTo || defendants[0] || 'Discovery').replace(/[^a-zA-Z0-9]/g, '_');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `AI_Discovery_${safeName}.docx`;
      a.click();
    } catch (err) {
      setError(err.message || 'Failed to generate the document.');
    } finally {
      setGenerating(false);
    }
  }

  // Index-tagged, type-grouped views for rendering (idx = position in `questions`).
  const tagged = questions.map((q, i) => ({ ...q, idx: i }));
  const interrogatories = tagged.filter((q) => q.type === 'interrogatory');
  const rfps = tagged.filter((q) => q.type === 'rfp');
  const includedCount = questions.filter((q) => q.include && q.text.trim()).length;

  const helpBox = {
    background: '#fff8e1', color: '#5b4a08', border: '1px solid #f0d68a',
    padding: 14, borderRadius: 4, marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13,
  };
  const qCard = {
    border: '1px solid #e2e2e2', borderRadius: 4, padding: 12, marginBottom: 10, background: '#fff',
  };

  // ── RENDER: UPLOAD ──────────────────────────────────────────────────────────
  if (phase === 'upload') {
    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          <h1 className="page-title">MightyDiscovery™ — AI discovery drafting</h1>
          <p className="page-subtitle">
            Upload a complaint and the AI drafts a set of interrogatories and requests for production to
            propound on the other party. You review and edit everything before it generates a document.
          </p>
        </div>

        {pricing && !pricing.exempt && (
          <div style={{
            background: '#fff8e1', color: '#5b4a08', border: '1px solid #f0d68a',
            padding: 14, borderRadius: 4, marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13, lineHeight: 1.5,
          }}>
            {pricing.freeRemaining > 0 ? (
              <>
                <strong>Each AI draft uses 1 AI action.</strong> Your firm has{' '}
                <strong>{pricing.freeRemaining} of {pricing.freeTotal} free AI actions</strong> remaining
                (shared with Document Module builds). After those, drafts cost{' '}
                <strong>1 AI credit</strong> each ({money(pricing.packPriceCents)} buys {pricing.packCredits} credits
                in Firm Settings → Billing). Scanned PDFs cost the same as text files — no OCR upcharge.
                You're only charged when drafting <strong>succeeds</strong> — never at download.
              </>
            ) : (
              <>
                <strong>Each AI draft uses 1 AI credit.</strong> Your firm's free AI actions are used up.
                Current balance: <strong>{pricing.creditBalance} credit{pricing.creditBalance === 1 ? '' : 's'}</strong>.
                {pricing.subscribed
                  ? <> Buy more in <strong>Firm Settings → Billing</strong> ({money(pricing.packPriceCents)} for {pricing.packCredits}).</>
                  : <> A LawMighty subscription ($29.99/month) is required to buy credits — see <strong>Firm Settings → Billing</strong>.</>}
                {' '}Scanned PDFs cost the same as text files — no OCR upcharge. You're only charged when
                drafting <strong>succeeds</strong> — never at download.
              </>
            )}
          </div>
        )}

        {error && <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

        <form onSubmit={onDraftClick}>
          <div className="section-head" style={{ marginTop: 0 }}>What are we drafting?</div>
          <div className="card">
            <div className="form-group">
              <label>Goal</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="defend">Defend — we represent the party the complaint is filed against</option>
                <option value="support">Support — we filed this complaint and want to prove it</option>
              </select>
              <div style={{ fontSize: 12, color: '#666', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                {mode === 'defend'
                  ? 'Discovery will test the allegations, probe the elements the other side must prove, and surface affirmative-defense angles.'
                  : 'Discovery will gather the evidence, witnesses, and documents needed to prove each element you have asserted.'}
              </div>
            </div>

            <div className="form-group">
              <label>Objectives (optional)</label>
              <textarea
                value={objectives}
                onChange={(e) => setObjectives(e.target.value)}
                rows={3}
                placeholder="Anything specific you want the discovery to focus on — e.g. 'dig into hidden income and undisclosed accounts', or 'they claim I had notice of the defect; develop the timeline'."
                style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }}
              />
              <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                The AI prioritizes these objectives but stays grounded in the complaint.
              </div>
            </div>
          </div>

          <div className="section-head">Documents</div>
          <div className="card">
            <div className="form-group">
              <label>Complaint *</label>
              <input type="file" accept=".pdf,.docx" onChange={(e) => setComplaintFile(e.target.files?.[0] || null)} />
              {complaintFile &&
                <div style={{ fontSize: 12, color: '#666', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                  Selected: <strong>{complaintFile.name}</strong> ({(complaintFile.size / 1024).toFixed(0)} KB)
                </div>}
              <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                PDF or .docx. Scanned PDFs work too — the tool will read them automatically, at the higher rate above.
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Supporting documents (optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <label style={{
                    display: 'inline-block', padding: '5px 12px', border: '1px solid #ccc',
                    borderRadius: 4, background: '#f5f5f5', cursor: 'pointer',
                    fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 'normal',
                  }}>
                    + Add files
                    <input
                      type="file"
                      accept=".pdf,.docx"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const incoming = Array.from(e.target.files || []);
                        setSupportingFiles((prev) => {
                          const existingNames = new Set(prev.map((f) => f.name + f.size));
                          const deduped = incoming.filter((f) => !existingNames.has(f.name + f.size));
                          return [...prev, ...deduped];
                        });
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {supportingFiles.length > 0 && (
                    <span style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                      {supportingFiles.length} file{supportingFiles.length !== 1 ? 's' : ''} queued
                    </span>
                  )}
                </div>
                {supportingFiles.length > 0 && (
                  <ul style={{
                    margin: '8px 0 0', padding: 0, listStyle: 'none',
                    border: '1px solid #e2e2e2', borderRadius: 4, overflow: 'hidden',
                  }}>
                    {supportingFiles.map((f, i) => (
                      <li key={f.name + f.size + i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderBottom: i < supportingFiles.length - 1 ? '1px solid #eee' : 'none',
                        background: '#fff', fontFamily: 'Arial, sans-serif', fontSize: 12,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                          📄 {f.name} <span style={{ color: '#aaa' }}>({(f.size / 1024).toFixed(0)} KB)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setSupportingFiles((prev) => prev.filter((_, j) => j !== i))}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#c00', fontWeight: 'bold', fontSize: 14, padding: '0 2px', flexShrink: 0,
                          }}
                          title="Remove"
                        >✕</button>
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                  Add files from any folder — click "+ Add files" multiple times to pick from different locations.
                </div>
              </div>
              <div className="form-group">
                <label>Style exemplar (optional)</label>
                <input type="file" accept=".pdf,.docx" onChange={(e) => setExemplarFile(e.target.files?.[0] || null)} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                  A past discovery doc — used for formatting/phrasing only, never its content.
                </div>
              </div>
            </div>
          </div>

          <div className="section-head">Case context (optional — helps the draft)</div>
          <div className="card">
            <div className="form-group">
              <label>Court</label>
              <input type="text" value={uploadCourt} onChange={(e) => setUploadCourt(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Plaintiff(s) *</label>
              {uploadPlaintiffs.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    value={p}
                    placeholder={`Plaintiff ${uploadPlaintiffs.length > 1 ? i + 1 : ''}`}
                    onChange={(e) => updateParty(setUploadPlaintiffs, i, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {uploadPlaintiffs.length > 1 && (
                    <button type="button" className="btn btn-sm" onClick={() => removeParty(setUploadPlaintiffs, i)}
                      style={{ color: '#c00', flexShrink: 0 }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-sm" onClick={() => addParty(setUploadPlaintiffs)}
                style={{ marginTop: 2 }}>+ Add plaintiff</button>
            </div>

            <div className="form-group">
              <label>Defendant(s) *</label>
              {uploadDefendants.map((d, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    value={d}
                    placeholder={`Defendant ${uploadDefendants.length > 1 ? i + 1 : ''}`}
                    onChange={(e) => updateParty(setUploadDefendants, i, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {uploadDefendants.length > 1 && (
                    <button type="button" className="btn btn-sm" onClick={() => removeParty(setUploadDefendants, i)}
                      style={{ color: '#c00', flexShrink: 0 }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-sm" onClick={() => addParty(setUploadDefendants)}
                style={{ marginTop: 2 }}>+ Add defendant</button>
            </div>

            {uploadCrossParties.length > 0 && uploadCrossParties.map((cp, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
                <input type="text" value={cp.name} placeholder="Party name"
                  onChange={(e) => { const updated = [...uploadCrossParties]; updated[i] = { ...updated[i], name: e.target.value }; setUploadCrossParties(updated); }}
                  style={{ flex: 2 }} />
                <input type="text" value={cp.role} placeholder="Role (e.g. Third-Party Defendant)"
                  onChange={(e) => { const updated = [...uploadCrossParties]; updated[i] = { ...updated[i], role: e.target.value }; setUploadCrossParties(updated); }}
                  style={{ flex: 2 }} />
                <button type="button" className="btn btn-sm" onClick={() => setUploadCrossParties((prev) => prev.filter((_, j) => j !== i))}
                  style={{ color: '#c00', flexShrink: 0 }}>✕</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm"
              onClick={() => setUploadCrossParties((prev) => [...prev, { name: '', role: '' }])}
              style={{ marginTop: 2 }}>+ Add cross/additional party</button>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button type="submit" className="btn btn-primary" disabled={drafting || !complaintFile}>
              {drafting ? <><span className="spinner" />Reading the complaint &amp; drafting…</> : 'Draft discovery with AI'}
            </button>
            <button type="button" className="btn" onClick={() => navigate('/admin/dashboard')} disabled={drafting}>
              Cancel
            </button>
          </div>
          {drafting &&
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 10 }}>
              This usually takes 15–40 seconds. The original files are not stored on the server.
            </p>}
        </form>

        {confirmOpen &&
          <div onClick={() => setConfirmOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 6, maxWidth: 460, width: '100%', padding: 20, fontFamily: 'Arial, sans-serif' }}>
              <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Generate AI discovery?</h2>
              <p style={{ fontSize: 13, color: '#444', lineHeight: 1.5, marginTop: 0 }}>
                {pricing && pricing.exempt ? (
                  <>This drafts discovery from your complaint. AI actions are included in your firm's plan.</>
                ) : pricing && pricing.freeRemaining > 0 ? (
                  <>
                    This drafts discovery from your complaint and uses <strong>1 of your{' '}
                    {pricing.freeRemaining} remaining free AI actions</strong> — only if drafting succeeds.
                    If it errors, nothing is used.
                  </>
                ) : (
                  <>
                    This drafts discovery from your complaint and uses <strong>1 AI credit</strong>
                    {pricing ? <> (balance: {pricing.creditBalance})</> : null} — only if drafting succeeds.
                    If it errors, no credit is used.
                  </>
                )}
              </p>
              {/* pkg75 — cost recovery: firm pays unless a client is picked */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10,
                fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                <span style={{ color: '#555' }}>Bill this draft to:</span>
                <select value={billClientId}
                  onFocus={() => { if (!billClients) adminApi.listClients().then(cs => setBillClients(Array.isArray(cs) ? cs : [])).catch(() => setBillClients([])); }}
                  onChange={e => setBillClientId(e.target.value)}
                  style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, maxWidth: 260 }}>
                  <option value="">— Firm pays —</option>
                  {(billClients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {billClientId && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    title="Added to the client as a billable expense — it will appear on their next invoice.">
                    as an expense · $
                    <input type="text" value={billAmount}
                      onChange={e => setBillAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                      style={{ width: 52, padding: '4px 6px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }} />
                  </label>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                <button type="button" className="btn" onClick={() => setConfirmOpen(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={runDraft}>
                  {pricing && pricing.exempt ? 'Yes, draft it (included)' : 'Yes, draft it'}
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    );
  }

  // ── RENDER: REVIEW ──────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-sm" onClick={startOver} style={{ marginBottom: 12 }}>← Start over</button>
        <h1 className="page-title">Review AI-Drafted Discovery</h1>
        <p className="page-subtitle">
          These are AI drafts based on the complaint. Edit, exclude, or delete anything, then fill the caption and generate.
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

      <div style={helpBox}>
        <strong>Review required.</strong> AI drafts can be wrong, over-broad, or miss your jurisdiction's limits
        (e.g. the cap on the number of interrogatories). Read and edit every item; you are responsible for the
        final document before it is served.
        {draftMeta && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Drafted {draftMeta.counts.interrogatories} interrogatories and {draftMeta.counts.requests} requests
            {draftMeta.exemplarSource && draftMeta.exemplarSource !== 'none' ? ` · style exemplar: ${draftMeta.exemplarSource}` : ''}
            {draftMeta.model ? ` · ${draftMeta.model}` : ''}.
          </div>
        )}
        {draftMeta && draftMeta.aiUsage && draftMeta.aiUsage.source !== 'exempt' && (
          <div style={{ marginTop: 6, fontSize: 12 }}>
            {draftMeta.aiUsage.source === 'free'
              ? <>This draft used a free AI action — <strong>{draftMeta.aiUsage.freeRemaining} free action{draftMeta.aiUsage.freeRemaining === 1 ? '' : 's'}</strong> remaining.</>
              : <>This draft used <strong>1 AI credit</strong> — {draftMeta.aiUsage.creditBalance} credit{draftMeta.aiUsage.creditBalance === 1 ? '' : 's'} remaining.</>}
          </div>
        )}
        {draftMeta && draftMeta.extractionWarnings &&
          <div style={{ marginTop: 6, fontSize: 12 }}>{draftMeta.extractionWarnings}</div>}
        {draftMeta && draftMeta.truncated && draftMeta.truncated.complaint &&
          <div style={{ marginTop: 6, fontSize: 12 }}>Note: the complaint was long and was truncated for drafting — double-check coverage.</div>}
      </div>

      {/* Interrogatories */}
      <div className="section-head" style={{ marginTop: 0 }}>Interrogatories ({interrogatories.length})</div>
      {interrogatories.length === 0 && <p style={{ color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>None drafted.</p>}
      {interrogatories.map((q, n) => (
        <div key={q.idx} style={{ ...qCard, opacity: q.include ? 1 : 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600 }}>
              <input type="checkbox" checked={q.include} onChange={(e) => updateQuestion(q.idx, { include: e.target.checked })} style={{ marginRight: 6 }} />
              Interrogatory No. {n + 1}
            </label>
            <button type="button" className="btn btn-sm" onClick={() => removeQuestion(q.idx)}>Remove</button>
          </div>
          <textarea value={q.text} onChange={(e) => updateQuestion(q.idx, { text: e.target.value })} rows={3}
            style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }} />
          {q.basis && <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontFamily: 'Arial, sans-serif' }}>Targets: {q.basis}</div>}
        </div>
      ))}

      {/* Requests for Production */}
      <div className="section-head">Requests for Production ({rfps.length})</div>
      {rfps.length === 0 && <p style={{ color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>None drafted.</p>}
      {rfps.map((q, n) => (
        <div key={q.idx} style={{ ...qCard, opacity: q.include ? 1 : 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600 }}>
              <input type="checkbox" checked={q.include} onChange={(e) => updateQuestion(q.idx, { include: e.target.checked })} style={{ marginRight: 6 }} />
              Request No. {n + 1}
            </label>
            <button type="button" className="btn btn-sm" onClick={() => removeQuestion(q.idx)}>Remove</button>
          </div>
          <textarea value={q.text} onChange={(e) => updateQuestion(q.idx, { text: e.target.value })} rows={3}
            style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }} />
          {q.basis && <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontFamily: 'Arial, sans-serif' }}>Targets: {q.basis}</div>}
        </div>
      ))}

      {/* Caption + signature for the generated document */}
      <div className="section-head">Document details</div>
      <div className="card">
        <div className="form-group">
          <label>Court</label>
          <input type="text" value={caseFields.court} onChange={(e) => setField('court', e.target.value)} />
        </div>

        <div className="form-group">
          <label>Plaintiff(s) *</label>
          {plaintiffs.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input type="text" value={p}
                placeholder={`Plaintiff ${plaintiffs.length > 1 ? i + 1 : ''}`}
                onChange={(e) => updateParty(setPlaintiffs, i, e.target.value)}
                style={{ flex: 1 }} />
              {plaintiffs.length > 1 && (
                <button type="button" className="btn btn-sm" onClick={() => removeParty(setPlaintiffs, i)}
                  style={{ color: '#c00', flexShrink: 0 }}>✕</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => addParty(setPlaintiffs)} style={{ marginTop: 2 }}>+ Add plaintiff</button>
        </div>

        <div className="form-group">
          <label>Defendant(s) *</label>
          {defendants.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input type="text" value={d}
                placeholder={`Defendant ${defendants.length > 1 ? i + 1 : ''}`}
                onChange={(e) => updateParty(setDefendants, i, e.target.value)}
                style={{ flex: 1 }} />
              {defendants.length > 1 && (
                <button type="button" className="btn btn-sm" onClick={() => removeParty(setDefendants, i)}
                  style={{ color: '#c00', flexShrink: 0 }}>✕</button>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => addParty(setDefendants)} style={{ marginTop: 2 }}>+ Add defendant</button>
        </div>

        {(crossParties.length > 0 || null) && (
          <div className="form-group">
            <label>Cross / Additional parties</label>
            {crossParties.map((cp, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
                <input type="text" value={cp.name} placeholder="Party name"
                  onChange={(e) => updateCrossParty(i, 'name', e.target.value)} style={{ flex: 2 }} />
                <input type="text" value={cp.role} placeholder="Role (e.g. Third-Party Defendant)"
                  onChange={(e) => updateCrossParty(i, 'role', e.target.value)} style={{ flex: 2 }} />
                <button type="button" className="btn btn-sm" onClick={() => removeCrossParty(i)}
                  style={{ color: '#c00', flexShrink: 0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <button type="button" className="btn btn-sm" onClick={addCrossParty} style={{ marginBottom: 14 }}>
          + Add cross/additional party
        </button>

        <div className="form-row">
          <div className="form-group">
            <label>Docket No.</label>
            <input type="text" value={caseFields.docketNo} onChange={(e) => setField('docketNo', e.target.value)} placeholder="___-D-____" />
          </div>
          <div className="form-group">
            <label>Division</label>
            <input type="text" value={caseFields.division} onChange={(e) => setField('division', e.target.value)} placeholder="__" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>We represent *</label>
            <select value={caseFields.representingParty} onChange={(e) => setField('representingParty', e.target.value)}>
              <option value="">— select —</option>
              {allPartyNames().map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Discovery directed to *</label>
            <select value={caseFields.directedTo} onChange={(e) => setField('directedTo', e.target.value)}>
              <option value="">— select —</option>
              {allPartyNames().filter((n) => n !== caseFields.representingParty).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Opposing Counsel (name &amp; address for the certificate of service)</label>
          <textarea value={caseFields.opposingCounsel} onChange={(e) => setField('opposingCounsel', e.target.value)} rows={3}
            placeholder={'Jane Doe, Esq.\nDoe & Associates\n123 Main St., Suite 200\nCity, ST 00000'}
            style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }} />
        </div>
      </div>

      <div className="section-head">Signing attorney(s)</div>
      <div className="card">
        {attorneys.length === 0 &&
          <p style={{ color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>No owners or attorneys found.</p>}
        {attorneys.map((a) => (
          <label key={a.id} style={{ display: 'block', fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 6 }}>
            <input type="checkbox" checked={attorneyIds.includes(a.id)} onChange={() => toggleAttorney(a.id)} style={{ marginRight: 6 }} />
            {a.name}{a.bar_number ? '' : ' (no bar number on file)'}
          </label>
        ))}
        {attorneyIds.length > 0 &&
          <div className="form-group" style={{ marginTop: 10 }}>
            <label>Signature ("By:") — whose name signs</label>
            <select value={signingAttorneyId} onChange={(e) => setSigningAttorneyId(e.target.value)}>
              {attorneys.filter((a) => attorneyIds.includes(a.id)).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} style={{ marginTop: 3 }} />
          <span>I have read and edited these requests, confirmed they fit this case and jurisdiction, and take professional responsibility for the document before it is served.</span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
        <button type="button" className="btn btn-primary" onClick={handleGenerate}
          disabled={generating || !acknowledged || includedCount === 0}>
          {generating ? <><span className="spinner" />Generating…</> : `Generate Word document (${includedCount} included)`}
        </button>
        <button type="button" className="btn" onClick={startOver} disabled={generating}>Start over</button>
      </div>
    </div>
  );
}
